import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { Trash2, Plus, CalendarDays } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only card on Preferences: manage public holidays for the active
 * organisation. Each row is a `{ date, name }` pair. Deleting removes the
 * holiday from every user's calendar and from the report's informational
 * "working days in range" count.
 */
export function PublicHolidaysCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingDate, setAddingDate] = useState("");
  const [addingName, setAddingName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/public-holidays");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!addingDate || !addingName.trim()) return;
    setSaving(true);
    try {
      await api.post("/public-holidays", {
        date: addingDate,
        name: addingName.trim(),
      });
      setAddingDate("");
      setAddingName("");
      toast.success("Public holiday added");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Remove "${it.name}" (${it.date})?`)) return;
    try {
      await api.delete(`/public-holidays/${it.id}`);
      toast.success("Public holiday removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Split into past and upcoming for a cleaner list.
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = items.filter((i) => i.date >= today);
  const past = items.filter((i) => i.date < today);

  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-xl p-6 space-y-5" data-testid="public-holidays-card">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-[#e8f1f8] text-[#145c8c] shrink-0">
          <CalendarDays className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-slate-700" style={{ fontFamily: "Manrope" }}>
            Public holidays
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Mark dates so they&apos;re shown on the calendar and excluded from the informational
            &quot;working days in range&quot; count on Reports. Applies to the current organisation.
          </p>
        </div>
      </div>

      {/* Add form */}
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,180px)_1fr_auto] gap-2 items-end pt-1">
        <div>
          <Label className="text-xs text-slate-500">Date</Label>
          <DatePicker
            value={addingDate}
            onChange={setAddingDate}
            className="h-9 mt-1 w-full"
            testId="ph-add-date"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Name</Label>
          <Input
            value={addingName}
            onChange={(e) => setAddingName(e.target.value)}
            placeholder="e.g. Christmas Day"
            className="h-9 mt-1"
            data-testid="ph-add-name"
          />
        </div>
        <Button
          onClick={add}
          disabled={saving || !addingDate || !addingName.trim()}
          className="h-9 text-white"
          style={{ background: "#145c8c" }}
          data-testid="ph-add-button"
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      {/* Lists */}
      {loading ? (
        <div className="text-sm text-slate-400 py-4 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">
          No public holidays yet.
        </div>
      ) : (
        <div className="space-y-4">
          {upcoming.length > 0 && (
            <PhSection title="Upcoming" items={upcoming} onDelete={remove} />
          )}
          {past.length > 0 && (
            <PhSection title="Past" items={past} onDelete={remove} muted />
          )}
        </div>
      )}
    </div>
  );
}

function PhSection({ title, items, onDelete, muted }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">{title}</div>
      <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
        {items.map((it) => (
          <li
            key={it.id}
            className={`flex items-center justify-between px-3 py-2 text-sm ${muted ? "text-slate-400 bg-slate-50" : "text-slate-700 bg-white"}`}
            data-testid={`ph-row-${it.id}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="font-mono text-xs w-24 shrink-0">{it.date}</span>
              <span className="truncate">{it.name}</span>
            </div>
            <button
              onClick={() => onDelete(it)}
              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
              data-testid={`ph-delete-${it.id}`}
              aria-label="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
